<template>
  <div>
    <b-card>
      <b-card-header>
        <b-card-title>Inventory Movement Over Time</b-card-title>
      </b-card-header>
      <b-card-body>
        <b-row class="mb-3">
          <b-col cols="12" md="6" lg="4">
            <label class="font-weight-bold">Select Product</label>
            <b-form-select 
              v-model="selectedProductId"
              :options="productOptions"
              @change="handleProductChange"
              size="sm"
            ></b-form-select>
          </b-col>
        </b-row>
        
        <div v-if="isLocalLoading" class="d-flex justify-content-center my-4">
          <b-spinner variant="primary" label="Loading..."></b-spinner>
        </div>
        
        <div v-else-if="chartData.length > 0">
          <apexchart
            type="line"
            :height="chartOptions.chart.height"
            :options="chartOptions"
            :series="series"
          />
        </div>
        <div v-else class="text-center py-5">
          <i class="fas fa-chart-line text-muted mb-3" style="font-size: 3rem;"></i>
          <h4 class="text-muted">No Inventory Movement Data Available</h4>
          <p>There is no inventory movement data for the selected product and date range.</p>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { 
  BCard, 
  BCardTitle, 
  BCardHeader, 
  BCardBody, 
  BFormSelect, 
  BRow, 
  BCol, 
  BSpinner 
} from "bootstrap-vue";
import { mapActions } from "vuex";

export default {
  name: "QuantityChart",
  components: {
    apexchart: VueApexCharts,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BFormSelect,
    BRow,
    BCol,
    BSpinner
  },
  props: {
    data: {
      type: Array,
      default: () => []
    },
    productList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedProductId: null,
      isLocalLoading: false,
      chartData: [] // Renamed from inventoryMovementData for clarity
    };
  },
  computed: {
    productOptions() {
      // Create options for select dropdown with a default "All Products" option
      const options = [
        { value: null, text: 'All Products' }
      ];
      
      if (Array.isArray(this.productList)) {
        this.productList.forEach(product => {
          if (product && product.id && product.name) {
            options.push({
              value: product.id,
              text: product.name
            });
          }
        });
      }
      
      return options;
    },
    
    formattedData() {
      // Always use the chartData for this component's display
      const sourceData = this.chartData.length > 0 ? this.chartData : this.data;
      
      if (!Array.isArray(sourceData) || sourceData.length === 0) {
        return [];
      }
      
      return sourceData.filter(item => 
        item && 
        item.name && 
        item.date && 
        item.amount !== undefined
      );
    },
    
    // Group data by transaction type (SALES, PURCHASE, PRODUCTION)
    groupedData() {
      const grouped = {};
      
      this.formattedData.forEach(item => {
        if (!grouped[item.name]) {
          grouped[item.name] = [];
        }
        
        // Parse date to extract month info
        const date = new Date(item.date);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        // Find if entry for this month already exists
        let monthEntry = grouped[item.name].find(entry => entry.month === monthYear);
        
        if (!monthEntry) {
          monthEntry = { month: monthYear, amount: 0 };
          grouped[item.name].push(monthEntry);
        }
        
        // Add the amount to the month total
        monthEntry.amount += Number(item.amount);
      });
      
      return grouped;
    },
    
    // Get all unique months from the data for x-axis categories
    monthCategories() {
      const months = new Set();
      
      this.formattedData.forEach(item => {
        const date = new Date(item.date);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        months.add(monthYear);
      });
      
      // Sort months chronologically
      return Array.from(months).sort();
    },
    
    // Prepare series data for the chart
    series() {
      const result = [];
      
      for (const [name, data] of Object.entries(this.groupedData)) {
        const seriesData = this.monthCategories.map(month => {
          const entry = data.find(d => d.month === month);
          return entry ? entry.amount : 0;
        });
        
        result.push({
          name: name,
          data: seriesData
        });
      }
      
      return result;
    },
    
    // Chart configuration options
    chartOptions() {
      return {
        chart: {
          height: 350,
          type: "line",
          zoom: {
            enabled: false
          },
          toolbar: {
            show: true
          }
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        dataLabels: {
          enabled: false
        },
        markers: {
          size: 4,
          hover: {
            size: 6
          }
        },
        grid: {
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5
          }
        },
        xaxis: {
          categories: this.monthCategories.map(month => {
            const [year, monthNum] = month.split('-');
            const date = new Date(Number(year), Number(monthNum) - 1);
            return date.toLocaleDateString('default', { month: 'short', year: 'numeric' });
          }),
          title: {
            text: "Month"
          }
        },
        yaxis: {
          title: {
            text: "Quantity"
          }
        },
        tooltip: {
          y: {
            formatter: function(value) {
              return value.toFixed(0);
            }
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'center'
        }
      };
    }
  },
  methods: {
    ...mapActions("reportModule", [
      "getInventoryQtyAnalysisReportAction"
    ]),
    
    async fetchProductData() {
      try {
        this.isLocalLoading = true;
        
        // Get date range from parent component
        const dateFrom = this.getDateFromParam();
        const dateTo = this.getDateToParam();
        
        if (!dateFrom || !dateTo) {
          console.warn("Date range not available for product filter");
          // Fall back to using the initial data prop
          this.chartData = this.data;
          return;
        }
        
        // Prepare query params
        const dateParams = {
          dateFrom,
          dateTo
        };
        
        // Add product ID if selected
        if (this.selectedProductId) {
          // Create a clone of dateParams to avoid modifying shared objects
          const productParams = { ...dateParams, itemId: this.selectedProductId };
          
          // Make a dedicated API call just for this chart that won't affect other charts
          const response = await this.getProductInventoryData(productParams);
          this.chartData = response || [];
        } else {
          // Use the data prop when no product is selected
          this.chartData = this.data;
        }
      } catch (error) {
        console.error("Error fetching inventory movement data:", error);
        // Fall back to the data prop on error
        this.chartData = this.data;
      } finally {
        this.isLocalLoading = false;
      }
    },
    
    // Specialized API call method that won't interfere with other components
    async getProductInventoryData(params) {
      try {
        // Using a dedicated endpoint or service for this specific chart
        // This avoids conflicts with the main data used by other charts
        const response = await this.getInventoryQtyAnalysisReportAction({
          ...params,
          _chartInstance: "productQuantityChart" // Flag to identify this particular call
        });
        return response;
      } catch (error) {
        console.error("API call error:", error);
        return [];
      }
    },
    
    async handleProductChange() {
      await this.fetchProductData();
    },
    
    getDateFromParam() {
      // Try to get date range from store or parent component
      if (this.$store && this.$store.state.reportModule && this.$store.state.reportModule.dateRange && this.$store.state.reportModule.dateRange[0]) {
        return this.formatDate(this.$store.state.reportModule.dateRange[0]);
      }
      
      // Fallback to current component's parent
      if (this.$parent && this.$parent.dateRange && this.$parent.dateRange[0]) {
        return this.formatDate(this.$parent.dateRange[0]);
      }
      
      return null;
    },
    
    getDateToParam() {
      // Try to get date range from store or parent component
      if (this.$store && this.$store.state.reportModule && this.$store.state.reportModule.dateRange && this.$store.state.reportModule.dateRange[1]) {
        return this.formatDate(this.$store.state.reportModule.dateRange[1]);
      }
      
      // Fallback to current component's parent
      if (this.$parent && this.$parent.dateRange && this.$parent.dateRange[1]) {
        return this.formatDate(this.$parent.dateRange[1]);
      }
      
      return null;
    },
    
    formatDate(date) {
      if (!date) return '';
      if (typeof date.toISOString === 'function') {
        return date.toISOString().split("T")[0];
      }
      return date;
    },
    
    selectFirstProduct() {
      if (Array.isArray(this.productList) && this.productList.length > 0) {
        // Find the first product with a valid ID
        const firstProduct = this.productList.find(product => product && product.id);
        if (firstProduct) {
          this.selectedProductId = firstProduct.id;
          // Fetch data for this product
          this.fetchProductData();
        }
      }
    }
  },
  watch: {
    // Watch for changes in productList to auto-select first product
    productList: {
      handler(newProducts) {
        if (Array.isArray(newProducts) && newProducts.length > 0 && !this.selectedProductId) {
          this.selectFirstProduct();
        }
      },
      immediate: false // Changed to false to prevent immediate execution
    },
    
    // Watch for changes in the data prop from parent
    data: {
      handler(newData) {
        // Only update if we don't have product-specific data yet
        if (!this.selectedProductId || this.chartData.length === 0) {
          this.chartData = newData;
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Initialize with the default data from props
    this.chartData = this.data || [];
    
    // If productList is already available, select first product after a delay
    // This delay ensures other components have finished their initial loading
    if (Array.isArray(this.productList) && this.productList.length > 0) {
      setTimeout(() => {
        this.selectFirstProduct();
      }, 500); // Short delay to let other components initialize first
    }
  }
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
}
</style>